/**
 * Map a resolved session context into the props expected by <App>.
 *
 * Handles both session shapes:
 *  - Generic `params` bag (frontend auto-conversion): keys match the original
 *    query-string params (opt, agency, senderG, order, ...).
 *  - Structured fields (bot worker path): branchId, processCodes, etc.
 *
 * The generic bag takes precedence when present, since it mirrors exactly what
 * the legacy query-string entry would have produced.
 */
export const mapSessionToAppProps = (session) => {
  const p = session.params || {};

  // Derive process codes string ("opt") from either the params bag or the
  // structured processCodes array.
  const optionQS =
    p.opt ||
    (Array.isArray(session.processCodes) && session.processCodes.length > 0
      ? session.processCodes.join(',')
      : '');

  const language =
    p.lang || session.language || undefined;

  return {
    optionQS: optionQS || undefined,
    agencyQS: p.agency || session.branchId || '',
    orderQS: p.order || session.transactionId || '',
    amountQS: p.amount || ' ',
    language,
    idSenderGlobal: p.senderG || session.senderUUID || '',
    nameSender: p.nameSender || '',
    // Default to '0' (not empty) when the caller (e.g. ViaLink) doesn't provide
    // a document id. '0' is the backend sentinel meaning "let the OCR generate
    // the documentId": document-extract-text uses the OCR-generated FILE uuid
    // when iddoc === '0'. An empty string would produce a blank documentId and
    // a malformed S3 filename (DOCUMENT_{sender}__F_...), breaking the ID link.
    iddoc: p.iddoc || '0',
    env: p.e || 'A',
    region: p.r || '',
    iris: p.i || '',
    selfie: p.se || (session.isSelfieCapture ? 'Y' : ''),
    idShortener: p.idS || '',
    isBackCapture: p.bk || (session.isBackCapture ? 'Y' : ''),
    documentTypeId: p.dt || '',
    username: p.username || '',
    reqsource: p.reqsource || '',
    transactionUuid: p.txUuid || session.transactionUuid || '',
    // ID-only flag for KYC (skip selfie). Comes from the params bag (`idonly`)
    // or the structured session field.
    idOnly: p.idonly || (session.skipSelfieOnKyc ? 'Y' : '')
  };
};

/**
 * Build the create-session request body from the current query params.
 * Only includes params that are present (non-empty).
 */
export const buildSessionParamsFromQuery = (searchParams) => {
  const keys = [
    'opt',
    'agency',
    'order',
    'amount',
    'lang',
    'senderG',
    'nameSender',
    'iddoc',
    'e',
    'r',
    'i',
    'se',
    'idS',
    'bk',
    'dt',
    'username',
    'reqsource',
    'txUuid',
    'idonly'
  ];

  const params = {};
  keys.forEach((k) => {
    const v = searchParams.get(k);
    if (v !== null && v !== '') params[k] = v;
  });
  return params;
};

const sessionMapper = { mapSessionToAppProps, buildSessionParamsFromQuery };
export default sessionMapper;
